/**
 * WatchlistSelector — Multi-Watchlist Phase 2 dropdown (Jul 6, 2026).
 *
 * Sits above the Watchlist view in Dashboard.js. Shows the currently-
 * active watchlist name (or "Main" for the unnamed placeholder) with a
 * chevron. Clicking expands a menu of every list the user owns, each
 * with pencil (rename) and trashcan (delete) affordances.
 *
 * Free tier
 *   Sees ONE list (their Main). Selector renders as a compact inline
 *   name with a pencil-only icon (no dropdown chevron, no trashcan, no
 *   "+ New Watchlist" button). Free users can name their Main watchlist
 *   but that's it.
 *
 * Plus tier
 *   Sees the full dropdown: Main + up to 4 custom lists, each with
 *   pencil + trashcan (Main can't be deleted — only cleared via the
 *   existing "Clear Watchlist" button at the bottom of the view). A
 *   "+ New Watchlist" button appears at the bottom of the dropdown
 *   when the user has < 4 customs. Lists in the 7-day downgrade grace
 *   period show a red "Read-only · deletes in Nd" chip and cannot be
 *   renamed or added-to.
 *
 * Atomic star repaint
 *   Store's setActive() dispatches `watchlist-changed` and
 *   `portfolio-changed` global events after the server confirms the
 *   switch. Every existing star button + count badge in the app
 *   already listens for those events and re-fetches, so switching
 *   automatically re-paints all stars app-wide.
 */

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronDown, Plus, Pencil, Trash2, Layers, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  getSnapshot, subscribe, hydrate, setActive, createWatchlist,
  renameWatchlist, deleteWatchlist,
} from '@/lib/watchlistStore';

/**
 * Compute the human "N day(s) left" chip for a pending-deletion list.
 * Returns null if the list is NOT pending deletion.
 */
function graceLabel(pendingIso) {
  if (!pendingIso) return null;
  const ms = new Date(pendingIso).getTime() - Date.now();
  const days = Math.max(0, Math.ceil(ms / 86400000));
  return days <= 1 ? 'deletes today' : `${days}d left`;
}

/**
 * Label a watchlist for display. Main starts unnamed — we surface it
 * as "Main" so the empty state has copy. Once renamed, the user's
 * label wins.
 */
function labelFor(list) {
  if (!list) return 'Main';
  if (list.name) return list.name;
  return list.is_main ? 'Main' : 'Untitled';
}

export default function WatchlistSelector() {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // watchlist_id pending delete
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const editInputRef = useRef(null);
  const newInputRef = useRef(null);
  const rootRef = useRef(null);
  // Round 99c (Jul 6, 2026) — mobile-only fixed positioning for the
  // dropdown menu. On phones the trigger is horizontally centered in
  // the toolbar, so an `absolute left-0` menu overflows the right
  // viewport edge and creates horizontal scroll. We compute the
  // trigger's bottom-Y on open and pin the menu with `fixed left-2
  // right-2 top=<computed>` so it hugs the LEFT viewport edge with
  // an 8px safe margin. Desktop unchanged (absolute left-0 w-72).
  const [mobileMenuTop, setMobileMenuTop] = useState(null);

  useEffect(() => {
    hydrate();
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.('[data-watchlist-selector-root]')) {
        setOpen(false);
        setEditingId(null);
        setCreating(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  useEffect(() => {
    if (creating && newInputRef.current) newInputRef.current.focus();
  }, [creating]);

  // Compute mobile menu Y-position anchored to trigger bottom. Runs
  // before browser paint via useLayoutEffect so there's no visual
  // flash at (0,0). Desktop returns null so responsive CSS kicks in.
  useLayoutEffect(() => {
    if (!open || typeof window === 'undefined') { setMobileMenuTop(null); return; }
    if (window.innerWidth >= 640) { setMobileMenuTop(null); return; }
    const r = rootRef.current?.getBoundingClientRect();
    if (r) setMobileMenuTop(r.bottom + 4);
  }, [open]);

  if (!store.loaded) return null;

  const active = store.lists.find((w) => w.is_active) || null;
  const main = store.lists.find((w) => w.is_main) || null;
  const customs = store.lists.filter((w) => !w.is_main);
  const atCap = customs.length >= (store.caps?.custom_watchlist_limit || 0);
  const activePendingDeletion = active?.pending_deletion_at || null;

  // ── Actions ────────────────────────────────────────────────────
  const onSelect = async (id) => {
    if (!id || id === store.activeId) { setOpen(false); return; }
    try {
      await setActive(id);
      const picked = store.lists.find((w) => w.id === id);
      toast.success(`Switched to "${labelFor(picked)}"`);
    } catch (_e) {
      toast.error("Couldn't switch watchlist");
    } finally {
      setOpen(false);
    }
  };

  const startRename = (list) => {
    setEditingId(list.id);
    setEditingName(list.name || '');
  };

  const commitRename = async () => {
    if (!editingId) return;
    const target = store.lists.find((w) => w.id === editingId);
    const clean = editingName.trim();
    // Main allows clearing back to empty (which flips it to unnamed
    // "Main" placeholder). Customs must have a name.
    if (!clean && !target?.is_main) {
      toast.error('Custom watchlists must have a name');
      return;
    }
    try {
      await renameWatchlist(editingId, clean || null);
      toast.success(clean ? `Renamed to "${clean}"` : 'Reset to Main');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't rename");
    } finally {
      setEditingId(null);
      setEditingName('');
      // Round 99b+ nit fix (Jul 6, 2026) — auto-close dropdown after
      // rename-save so the user immediately sees the updated label on
      // the trigger pill. Testing agent flagged this as UX-polish.
      setOpen(false);
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const commitDelete = async (id) => {
    try {
      const res = await deleteWatchlist(id);
      const n = res?.deleted_entries || 0;
      toast.success(
        n > 0
          ? `Deleted watchlist · ${n} ticker${n === 1 ? '' : 's'} removed`
          : 'Deleted watchlist',
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    } finally {
      setConfirmDelete(null);
    }
  };

  const commitCreate = async () => {
    const clean = newName.trim();
    if (!clean) { toast.error('Give your watchlist a name'); return; }
    try {
      const created = await createWatchlist(clean);
      toast.success(`Created "${clean}" — switching…`);
      await setActive(created.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create");
    } finally {
      setCreating(false);
      setNewName('');
      setOpen(false);
    }
  };

  // ── Free-tier compact mode ────────────────────────────────────
  // Free users see only their Main + a pencil to name it. No
  // dropdown, no create/delete affordances.
  if (!store.isPlus) {
    if (!main) return null;
    return (
      <div
        ref={rootRef}
        className="relative inline-flex items-center gap-2"
        data-watchlist-selector-root
        data-testid="watchlist-selector-free"
      >
        {editingId === main.id ? (
          <>
            <input
              ref={editInputRef}
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') cancelRename();
              }}
              maxLength={27}
              placeholder="Name your watchlist"
              className="text-sm font-bold px-2 py-1 rounded border border-fuchsia-400 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
              data-testid="watchlist-rename-input"
            />
            <button
              type="button"
              onClick={commitRename}
              className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
              aria-label="Save name"
              data-testid="watchlist-rename-save"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={cancelRename}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
              aria-label="Cancel rename"
              data-testid="watchlist-rename-cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <span className="text-sm font-bold" data-testid="watchlist-selector-active-name">
              {labelFor(main)}
            </span>
            <button
              type="button"
              onClick={() => startRename(main)}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-fuchsia-600 transition-colors"
              aria-label={main.name ? 'Rename watchlist' : 'Name your watchlist'}
              title={main.name ? 'Rename watchlist' : 'Name your watchlist'}
              data-testid="watchlist-rename-btn"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Plus-tier dropdown ────────────────────────────────────────
  return (
    <div
      ref={rootRef}
      className="relative inline-block"
      data-watchlist-selector-root
      data-testid="watchlist-selector"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold border transition-colors ${
          activePendingDeletion
            ? 'border-red-300 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50'
            : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-fuchsia-400 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/30'
        }`}
        aria-expanded={open}
        data-testid="watchlist-selector-trigger"
      >
        <Layers className="w-4 h-4" />
        <span data-testid="watchlist-selector-active-name">
          {labelFor(active)}
        </span>
        {active && (
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-normal">
            {active.entry_count}/{store.caps.watchlist_limit}
          </span>
        )}
        {activePendingDeletion && (
          <span className="text-[10px] font-bold text-red-700 dark:text-red-300" data-testid="watchlist-selector-grace-chip">
            · {graceLabel(activePendingDeletion)}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={`z-40 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden ${
            mobileMenuTop !== null
              ? 'fixed left-2 right-2'
              : 'absolute left-0 mt-1 w-72'
          }`}
          style={mobileMenuTop !== null ? { top: mobileMenuTop } : undefined}
          data-testid="watchlist-selector-menu"
        >
          <ul className="max-h-80 overflow-y-auto">
            {store.lists.map((list) => {
              const isEditing = editingId === list.id;
              const grace = graceLabel(list.pending_deletion_at);
              return (
                <li
                  key={list.id}
                  className={`flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0 ${
                    list.is_active ? 'bg-fuchsia-50 dark:bg-fuchsia-950/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                  data-testid={`watchlist-selector-row-${list.is_main ? 'main' : list.id}`}
                >
                  {isEditing ? (
                    <>
                      <input
                        ref={editInputRef}
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitRename();
                          if (e.key === 'Escape') cancelRename();
                        }}
                        maxLength={27}
                        placeholder={list.is_main ? 'Main (leave blank)' : 'List name'}
                        className="flex-1 text-sm font-bold px-2 py-1 rounded border border-fuchsia-400 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                        data-testid="watchlist-rename-input"
                      />
                      <button
                        type="button"
                        onClick={commitRename}
                        className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                        aria-label="Save"
                        data-testid="watchlist-rename-save"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelRename}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                        aria-label="Cancel"
                        data-testid="watchlist-rename-cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onSelect(list.id)}
                        className="flex-1 flex items-center gap-2 text-left"
                        data-testid={`watchlist-selector-pick-${list.is_main ? 'main' : list.id}`}
                      >
                        <span className={`text-sm ${list.is_active ? 'font-bold text-fuchsia-700 dark:text-fuchsia-300' : 'text-slate-800 dark:text-slate-200'}`}>
                          {labelFor(list)}
                        </span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">
                          {list.entry_count}
                        </span>
                        {grace && (
                          <span className="text-[10px] font-bold text-red-600 dark:text-red-400 inline-flex items-center gap-0.5">
                            <AlertTriangle className="w-3 h-3" /> {grace}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); startRename(list); }}
                        disabled={!!list.pending_deletion_at}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-fuchsia-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
                        aria-label={list.name ? 'Rename' : 'Name'}
                        title={list.pending_deletion_at ? 'Re-upgrade to Plus to rename' : (list.name ? 'Rename' : 'Name your watchlist')}
                        data-testid={`watchlist-rename-btn-${list.is_main ? 'main' : list.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {!list.is_main && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(list.id); }}
                          className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-950/40 text-slate-500 hover:text-red-600 transition-colors"
                          aria-label="Delete watchlist"
                          title="Delete watchlist"
                          data-testid={`watchlist-delete-btn-${list.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          {/* + New Watchlist */}
          {creating ? (
            <div className="flex items-center gap-2 px-2 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <input
                ref={newInputRef}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitCreate();
                  if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                }}
                maxLength={27}
                placeholder="Name your new watchlist"
                className="flex-1 text-sm px-2 py-1 rounded border border-fuchsia-400 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                data-testid="watchlist-create-input"
              />
              <button
                type="button"
                onClick={commitCreate}
                className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                aria-label="Create"
                data-testid="watchlist-create-save"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => { setCreating(false); setNewName(''); }}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
                aria-label="Cancel"
                data-testid="watchlist-create-cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              disabled={atCap}
              className="w-full flex items-center gap-2 px-3 py-2 border-t border-slate-200 dark:border-slate-700 text-sm font-bold text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-950/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              data-testid="watchlist-create-btn"
              title={atCap ? `Plus tier caps at ${store.caps.custom_watchlist_limit} custom watchlists` : 'Create a new watchlist'}
            >
              <Plus className="w-4 h-4" />
              {atCap ? `At limit (${customs.length}/${store.caps.custom_watchlist_limit})` : 'New Watchlist'}
            </button>
          )}
        </div>
      )}

      {/* Delete confirmation modal — matches the PortfolioSelector pattern
          so it feels native to users who've used that surface. */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="watchlist-delete-confirm-backdrop"
        >
          <div
            className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-red-300 dark:border-red-800 p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="watchlist-delete-confirm-modal"
          >
            <h3 className="text-lg font-bold text-red-700 dark:text-red-300 mb-2 inline-flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete watchlist?
            </h3>
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">
              {(() => {
                const l = store.lists.find((w) => w.id === confirmDelete);
                const n = l?.entry_count || 0;
                return (
                  <>
                    You&apos;re about to delete <b>&ldquo;{labelFor(l)}&rdquo;</b> and its{' '}
                    <b>{n} ticker{n === 1 ? '' : 's'}</b>. This can&apos;t be undone. Any
                    stars ★ on those tickers that aren&apos;t on your other lists will
                    also be removed.
                  </>
                );
              })()}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg text-sm font-bold border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="watchlist-delete-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => commitDelete(confirmDelete)}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 text-white hover:bg-red-700"
                data-testid="watchlist-delete-confirm"
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
